import {computed, makeObservable, observable} from "mobx";
import {GameInfo} from "../games/game-info";
import {IServiceFactoryExtended} from "../service-factory-extended.interface";
import {ServiceBase} from "../ServiceBase";
import {ISlideRenderer, IVideoSlidesService, IVideoSlideStoredData} from "./video-slides.service.interface";
import {ApiListType, ArenaConfiguration, IVideoSlidesFilter} from "../configurations/config-data-sections/arenas.configuration";
import {VIDEO_SLIDES_ARENA} from "../arenas/arena-consts";
import {VideoSlidesFilterModel} from "./models/video-slides-filter-editor/video-slides-filter.model";
import {ForYouVideoSlidesFilter} from "./models/video-slides-filter-editor/for-you-video-slides-filter";
import {SlidesRulesHandler} from "./models/rules/slides-rules-handler";
import {Nullable} from "../../../common/types/nullable-types";
import {Check} from "@sparkware/app-utils";
import {ISparkSwiperController} from "../../components/spark-swiper/models/spark-swiper-controller.interface";
import {VideoSlidesSwiper} from "./models/swiper/video-slides-swiper";
import {LocalStorageKeys} from "../storage/local-storage-keys";
import {IVideoPlayerViewModel} from "../video/models/video-player.view-model.interface";
import {ILoggerService} from "../logger/logger.service.interface";


export class VideoSlidesService extends ServiceBase implements IVideoSlidesService {

    constructor(services: IServiceFactoryExtended) {
        super(services);

        this._videoSlidesFilters = this._createVideoSlidesFilters();
        this._loadGameTypes();

        makeObservable(this);

        this._swiper = new VideoSlidesSwiper(this);
    }

    private _isAiSortingEnabled(): boolean {
        return this.services.configuration.servicesConfigs.VideoSlides?.useAiSorting;
    }

    private readonly _swiper: VideoSlidesSwiper;

    private _isVideoSlidesEnabled(): boolean {
        const videoSlidesConfigs = this.services.configuration.servicesConfigs.VideoSlides;
        return (
            videoSlidesConfigs.force ||
            (this.services.abTestingFeatureDataProvider.isVideoSlidesAbEnabled && videoSlidesConfigs.enabled)
        );
    }

    private _isStrictlyMobileDevice(): boolean {
        return this.services.device.model.deviceType === "mobile";
    }

    get shouldBeDisplayed(): boolean {
        return (
            !this.services.miniOrbit.isEnabled &&
            this._isStrictlyMobileDevice() &&
            this.services.user.isAuthorized &&
            this._isVideoSlidesEnabled());
    }

    @observable.ref
    private _gamesIdsWithStaticVideos: number[] = [];

    @observable.ref
    private _gamesIdsWithLiveStreamVideos: number[] = [];


    private get config(): ArenaConfiguration {
        return this.services.configuration.data.arenas[VIDEO_SLIDES_ARENA];
    }


    private _getGamesInfo(gameTypes: number[], condition: (gameInfo: GameInfo) => boolean): GameInfo[] {
        const gamesMap = this.services.games.gamesMap;
        return gameTypes.reduce((accumulator, currentGameType) => {
            const gameInfo = gamesMap[currentGameType];
            if (gameInfo && condition(gameInfo)) {
                accumulator.push(gameInfo);
            }
            return accumulator;
        }, []);
    }


    @computed
    private get gamesWithStaticVideos(): GameInfo[] {
        let gameTypes = this._gamesIdsWithStaticVideos;
        if (this._isAiSortingEnabled()) {
            gameTypes = this.services.aiEngine.sortGames(gameTypes);
        }
        return this._getGamesInfo(gameTypes, gameInfo => Boolean(gameInfo.videoSlideUrl));
    }

    @computed
    private get gamesWithLiveStreamVideos(): GameInfo[] {
        let gameTypes = this._gamesIdsWithLiveStreamVideos;
        if (this._isAiSortingEnabled()) {
            gameTypes = this.services.aiEngine.sortGames(gameTypes);
        }
        return this._getGamesInfo(gameTypes, gameInfo => Boolean(gameInfo.videoURL));
    }

    @computed
    get allGamesInArena(): GameInfo[] {
        return [
            ...this.gamesWithStaticVideos,
            ...this.gamesWithLiveStreamVideos
        ];
    }


    private _filterGamesInfo(gamesInfo: GameInfo[]): GameInfo[] {
        const filteredGamesMap = this.services.gamesFilter.filteredGamesMap;
        return gamesInfo.filter(gInfo => Boolean(filteredGamesMap[gInfo.gameType]));
    }


    @computed
    get slidesRenderers(): ISlideRenderer[] {
        const gamesWithStaticVideos = this._filterGamesInfo(this.gamesWithStaticVideos);
        const gamesWithLiveStreamVideos = this._filterGamesInfo(this.gamesWithLiveStreamVideos);
        const rulesHandler = new SlidesRulesHandler(this.services, this.config.slidesRules, gamesWithStaticVideos, gamesWithLiveStreamVideos);
        return rulesHandler.createRenderers();
    }


    private readonly _videoSlidesFilters: VideoSlidesFilterModel[] = [];
    get videoSlidesFilters(): VideoSlidesFilterModel[] {
        return this._videoSlidesFilters;
    }

    @computed
    get currentAppliedFilter(): VideoSlidesFilterModel {
        return this.videoSlidesFilters.find(f => f.on);
    }

    private _createVideoSlidesFilters(): VideoSlidesFilterModel[] {
        const filters: IVideoSlidesFilter[] = this.config?.filters ?? [];
        return [
            new ForYouVideoSlidesFilter(this.services, {id: "forYou", category: "forYou", translationKey: "For You", icon: "exploreOutlinedIcon"}),
            ...filters.map((filter) => new VideoSlidesFilterModel(this.services, filter))
        ];
    }

    private _loadGameTypes(): void {
        this.config.swiperGamesList?.forEach(swiperList => {
            switch (swiperList.apiListType) {
                case ApiListType.STATIC_VIDEO:
                    break;
                case ApiListType.LIVE_CASINO_VIDEO:
                    this._gamesIdsWithLiveStreamVideos = swiperList.gamesId;
                    break;
                default:
                    //this will be changed to a dedicated new API List type
                    this._gamesIdsWithStaticVideos = swiperList.gamesId;
                    break;
            }
        });
    }

    firstGameForFilter(filterId: string): Nullable<GameInfo> {
        let foundGame: Nullable<GameInfo> = null;
        this.allGamesInArena.some(currentGame => {
            const filterListForCurrentGame = this.services.gamesFilter.getFiltersForGame(currentGame.gameType);
            let shouldStopSearching = false;
            if (!Check.isNullOrUndefined(filterListForCurrentGame)) {
                shouldStopSearching = filterListForCurrentGame.some(filterForCurrentGame => {
                    if (filterForCurrentGame === filterId) {
                        foundGame = currentGame;
                        return true;
                    }
                    return false;
                });

            }
            return shouldStopSearching;
        })

        return foundGame;
    }

    navigateToFilter(filterId?: string): void {
        if (Check.isNullOrUndefined(filterId)) {
            this.services.location.push(`/video-slides`);
        } else {
            this.services.location.push(`/video-slides#filters=${filterId}`);
        }
    }

    get onBoardingWasShown(): boolean {
        return this.getStorageData().onBoardingShown;
    }

    set onBoardingWasShown(value: boolean){

        this.setStorageData({
            onBoardingShown: value
        });
    }

    get swiperController(): ISparkSwiperController {
        return this._swiper.swiperController;
    }

    private _storageData: IVideoSlideStoredData | null = null;
    getStorageData(): IVideoSlideStoredData {
        if(!this._storageData) {
            this._storageData = {
                lastIndexPerFilter: {},
                onBoardingShown: false,
                ...this.services.localStorage.getJson<IVideoSlideStoredData>(LocalStorageKeys.videoSlides)
            };
        }
        return this._storageData;

    }
    setStorageData(data: Partial<IVideoSlideStoredData>) {
        this._storageData = {
            ...this.getStorageData(),
            ...data
        };
        this.services.localStorage.setJson(LocalStorageKeys.videoSlides, this._storageData);
    }

    createVideoPlayer(): IVideoPlayerViewModel {
        return this.services.video.createVideoStreamingPlayer();
    }

    get logger(): ILoggerService {
        return this.services.logger;
    }

    fireExplorerSwipeEvent(): void {
        this.services.sdk.eventsTrigger.exploreSwipe();
    }
}



